import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader'
import AppButton from '../../components/AppButton/AppButton';
import AppInput from '../../components/AppInput/AppInput';
import { useSelector, useDispatch } from 'react-redux';
import { Armchair, Hash, Loader2 } from 'lucide-react';
import AppSelect from '../../components/AppSelect/AppSelect';
import { setApiErrorJson, setApiJson } from '../../redux/Actions/ApiAction';
import { SeatValidationSchema } from '../../validationscheema/SeatValidation';
import { addSeat, updateSeat, searchSeat } from '../../constant/Constant';
import { HitApi } from '../../Api/ApiHit';
import {
    fetchMultipleGeneralOptions,
    getOptionsWithFallback
} from '../../utils/generalOptionsUtils';
import { getSelectedUnit } from '../../storage/Storage';
import SearchableSelect from '../../components/AppSelect/SearchableSelect';
import toast from 'react-hot-toast';
import { getErrorMsg, hasError } from '../../utils/utils';

function AddSeat() {
    const [isEditMode, setIsEditMode] = useState(false);
    const [genderOptionsFromApi, setGenderOptionsFromApi] = useState([]);
    const [seatStatusOptionsFromApi, setSeatStatusOptionsFromApi] = useState([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: urlId } = useParams();

    const getUnit = getSelectedUnit()

    const loadOptionsAtOnce = async () => {
        setIsLoadingOptions(true);
        try {
            const optionsData = await fetchMultipleGeneralOptions(['Gender', 'SeatStatus']);

            setGenderOptionsFromApi(optionsData.Gender || []);
            setSeatStatusOptionsFromApi(optionsData.SeatStatus || []);
        } catch (error) {
            console.error('Error loading options:', error);
            toast.error('Failed to load form options. Please refresh and try again.');
        } finally {
            setIsLoadingOptions(false);
        }
    };

    // Generate random seat code
    const generateRandomSeatCode = () => {
        const randomNumber = Math.floor(Math.random() * 9999) + 1;
        const formattedNumber = randomNumber.toString().padStart(4, '0');
        return `ELV${formattedNumber}`;
    };

    useEffect(() => {
        const initialSeatData = {
            seatName: "",
            seatFor: "",
            seatCode: generateRandomSeatCode(),
            status: ''
        };

        if (urlId) {
            setIsEditMode(true);
            loadData();
        } else {
            dispatch(setApiJson(initialSeatData));
            dispatch(setApiErrorJson({}));
        }

        loadOptionsAtOnce();
    }, [dispatch, urlId]);

    const loadData = () => {
        setIsLoadingData(true);
        var json = {
            page: 1,
            limit: 10,
            search: {
                _id: urlId,
                unitIds: getUnit?._id
            }
        };

        HitApi(json, searchSeat).then((res) => {
            if (res?.message === "Seats retrieved successfully") {
                const seatData = res?.data?.[0];

                if (seatData) {
                    dispatch(setApiJson(seatData));
                } else {
                    toast.error('Seat not found');
                    navigate('/seat');
                }
            } else {
                toast.error('Failed to load seat data');
                navigate('/seat');
            }
        }).catch((error) => {
            toast.error('Error loading seat data. Please try again.');
            navigate('/seat');
        }).finally(() => {
            setIsLoadingData(false);
        });
    };

    const handleSubmit = () => {


        setIsSubmitting(true);

        SeatValidationSchema(ApiReducer?.apiJson).then((errors) => {
            console.log("errors", errors);

            if (!urlId) {
                delete errors.status;
            }

            dispatch(setApiErrorJson(errors));

            if (Object.keys(errors).length === 0) {
                const seatData = { ...ApiReducer.apiJson, unitIds: getUnit?._id };
                const addSeatData = { ...ApiReducer.apiJson, unitIds: getUnit?._id, status: "Initiated" }

                if (isEditMode) {
                    HitApi(seatData, updateSeat).then((res) => {
                        if (res?.message === "Seat updated successfully" || res?.success) {
                            toast.success('Seat updated successfully!');
                            navigate('/seat');
                        } else {
                            toast.error(res?.message || "Failed to update seat");
                        }
                    }).catch((error) => {
                        console.error("Update error:", error);
                        toast.error("Error updating seat. Please try again.");
                    }).finally(() => {
                        setIsSubmitting(false);
                    });
                } else {
                    HitApi(addSeatData, addSeat).then((res) => {
                        if (res?.message === "Seat created successfully" || res?.success) {
                            toast.success('Seat created successfully!');
                            navigate('/seat');
                        } else {
                            toast.error(res?.message || "Failed to create seat");
                        }
                    }).catch((error) => {
                        toast.error("Error creating seat. Please try again.");
                    }).finally(() => {
                        setIsSubmitting(false);
                    });
                }
            } else {
                setIsSubmitting(false);
            }
        }).catch((validationError) => {
            setIsSubmitting(false);
        });
    }

    if (isLoadingData) {
        return (
            <div className='p-5'>
                <PageHeader
                    title={isEditMode ? 'Edit Seat' : 'Add Seat'}
                    description={isEditMode ? 'Update existing salon seat configuration' : 'Create and configure new salon seat arrangements'}
                />

            </div>
        );
    }

    console.log("ApiReducer", ApiReducer);

    return (
        <div className='p-5'>
            <PageHeader
                title={isEditMode ? 'Edit Seat' : 'Add Seat'}
                description={isEditMode ? 'Update existing salon seat configuration' : 'Create and configure new salon seat arrangements'}
            />

            <div className="bg-white p-8 border rounded-xl mb-6">
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Seat Information</span>
                </div>

                {/* Basic Seat Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <AppInput
                        title="Seat Code"
                        placeholder="Auto-generated"
                        name="seatCode"
                        disabled={true}
                        important={true}
                        icon={<Hash size={18} />}
                    />
                    <AppInput
                        title="Seat Name"
                        placeholder="e.g., Seat-01"
                        name="seatName"
                        important={true}
                        error={hasError('seatName', ApiReducer)}
                        errormsg={getErrorMsg('seatName', ApiReducer)}
                        icon={<Armchair size={18} />}
                        disabled={isSubmitting}
                    />
                    <SearchableSelect
                        title="Seat For"
                        name="seatFor"
                        options={getOptionsWithFallback(genderOptionsFromApi)}
                        important={true}
                        error={hasError('seatFor', ApiReducer)}
                        errormsg={getErrorMsg('seatFor', ApiReducer)}
                        disabled={isLoadingOptions || isSubmitting}
                    />
                    {urlId &&
                        <SearchableSelect
                            title="Seat Status"
                            name="status"
                            options={getOptionsWithFallback(seatStatusOptionsFromApi)}
                            important={true}
                            error={hasError('status', ApiReducer)}
                            errormsg={getErrorMsg('status', ApiReducer)}
                            disabled={isLoadingOptions || isSubmitting}
                        />
                    }
                </div>

                <div className='mt-5 flex justify-end gap-3'>
                    <AppButton
                        buttontext={isSubmitting ? "Processing..." : (isEditMode ? "Update Seat" : "Add Seat")}
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoadingOptions}
                        isLoading={isSubmitting}
                    />
                </div>
            </div>
        </div>
    )
}

export default AddSeat